// src/components/learning/CourseList.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Terminal, 
  Book,
  ArrowRight,
  Box,
  Zap,
  Users,
  Star
} from 'lucide-react';
import { motion } from 'framer-motion';
import { courseApi } from '../../services/courseApi';

const courseIcons = {
  linux: Terminal,
  ansible: Box,
  default: Book
};

const features = [
  {
    icon: Zap,
    title: 'Learn by Doing',
    description: 'Hands-on labs and real-world projects to reinforce your learning',
    iconColor: 'text-blue-400',
    baseGradient: 'from-blue-500/10 to-blue-600/10',
    hoverGradient: 'hover:from-blue-500/20 hover:to-blue-600/20',
    border: 'border-blue-500/20 hover:border-blue-500/30'
  },
  {
    icon: Users,
    title: 'Community Support',
    description: 'Join a community of developers learning and growing together',
    iconColor: 'text-purple-400',
    baseGradient: 'from-purple-500/10 to-purple-600/10',
    hoverGradient: 'hover:from-purple-500/20 hover:to-purple-600/20',
    border: 'border-purple-500/20 hover:border-purple-500/30'
  },
  {
    icon: Star,
    title: 'Expert Content',
    description: 'Courses designed and reviewed by industry professionals',
    iconColor: 'text-amber-400',
    baseGradient: 'from-amber-500/10 to-amber-600/10',
    hoverGradient: 'hover:from-amber-500/20 hover:to-amber-600/20',
    border: 'border-amber-500/20 hover:border-amber-500/30'
  }
];

const CourseList = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await courseApi.getAllCourses();
        setCourses(response.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-900 flex items-center justify-center">
        <div className="space-y-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"/>
          <p className="text-gray-400 animate-pulse">Loading courses...</p>
        </div>
      </div>
    );
  }

  const courseVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-900">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-6xl font-bold text-white mb-8">
              DevOps Learning Paths
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Master essential DevOps skills through hands-on courses designed for modern engineers
            </p>
          </div>
        </div>
      </motion.div>

      {/* Course Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((course, index) => {
            const IconComponent = courseIcons[course.courseId] || courseIcons.default;
            
            return (
              <motion.div
                key={course.courseId}
                variants={courseVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={`/learning/courses/${course.courseId}`}
                  className="block group relative no-underline"
                >
                  <div className="relative bg-gray-800/40 backdrop-blur-xl rounded-2xl p-6 ring-1 ring-gray-700/50 hover:ring-blue-500/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10">
                    <div className="mb-6">
                      <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 ring-1 ring-blue-500/20">
                        <IconComponent className="w-8 h-8 text-blue-400" />
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-gray-400 mb-6 line-clamp-2">
                      {course.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400">
                        {course.difficulty}
                      </span>
                      <span className="flex items-center text-blue-400 text-sm font-medium">
                        Start Course
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Features Section */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={courseVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.3 + index * 0.1 }}
              className="group"
            >
              <div className={`
                relative bg-gray-800/40 backdrop-blur-xl rounded-2xl p-6
                transition-all duration-300
                hover:-translate-y-1 hover:shadow-xl
                border ${feature.border}
                bg-gradient-to-br ${feature.baseGradient} ${feature.hoverGradient}
                hover:ring-1 hover:ring-${feature.iconColor.split('-')[1]}-500/50
              `}>
                <div className={`
                  inline-flex p-3 rounded-xl mb-4
                  bg-gradient-to-br ${feature.baseGradient}
                  border ${feature.border}
                  group-hover:scale-110 transition-transform duration-300
                `}>
                  <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
                </div>
                <h3 className={`text-lg font-semibold text-white mb-2 group-hover:text-${feature.iconColor.split('-')[1]}-400 transition-colors`}>
                  {feature.title}
                </h3>
                <p className="text-gray-300 line-height-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom Spacing */}
      <div className="h-24"></div>
    </div>
  );
};

export default CourseList;